import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import styles from './ChassisMap.module.css';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronRightIcon,
  LockClosedIcon,
  LockOpenIcon,
} from '@heroicons/react/24/outline';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import GridLabel from '../../components/GridLabel';
import { Drawer } from '../../components/ui/Drawer';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import api from '../../services/api';
import { getGoogleMapsLoaderOptions } from '../../config/googleMaps';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { matchChipText, CONFIDENCE_CLASS } from './components/ChassisMapPanel';

// ============================================================================
// TYPES
// ============================================================================

interface ChassisData {
  asset_id: string;
  identifier: string;
  asset_class: string | null;
  chassis_type: string | null;
  door_type: string | null;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  velocity: number | null;
  is_moving: boolean | null;
  battery_state: boolean | null;
  has_contents: boolean | null;
  contents_percentage: number | null;
  door_open: boolean | null;
  temperature: number | null;
  humidity: number | null;
  last_reported_at: string | null;
  last_synced_at: string | null;
  equip_category: string | null;
  equip_make: string | null;
  equip_model: string | null;
  active_pronumber: number | null;
  active_container: string | null;
  matched_truck_identifier: string | null;
  matched_tag_serial: string | null;
  match_confidence: 'confirmed' | 'probable' | 'stale' | null;
  match_score: number | null;
  matched_since: string | null;
}

interface ChassisListRow extends ChassisData {
  rowKey: string;
}

// ============================================================================
// MAP CONFIGURATION
// ============================================================================

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 29.7604,
  lng: -95.3698,
};

const getMapOptions = (isDarkMode: boolean): google.maps.MapOptions => ({
  mapId: '3cf64d0fcebdaf11f3625d6e',
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  clickableIcons: false,
  colorScheme: isDarkMode ? 'DARK' : 'LIGHT',
});

const REFRESH_INTERVAL_MS = 60_000;

// ============================================================================
// MARKER HELPERS
// ============================================================================

function getChassisColor(chassis: ChassisData): string {
  if (chassis.battery_state === false) return '#EF4444'; // red - low battery
  if (chassis.door_open === true) return '#F59E0B';      // orange - door open
  if (chassis.is_moving === true) return '#10B981';       // green - moving
  return '#6B7280';                                        // grey - stationary
}

function createChassisMarkerContent(chassis: ChassisData, isSelected: boolean): HTMLDivElement {
  const color = getChassisColor(chassis);
  const container = document.createElement('div');
  container.style.cursor = 'pointer';
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.alignItems = 'center';

  // Build label: unit number + (93K) if model contains it
  let label = chassis.identifier || '';
  if (chassis.equip_model && /93k/i.test(chassis.equip_model)) {
    label += ' (93K)';
  }

  const selectedStyle = isSelected
    ? 'outline: 2px solid #3B82F6; outline-offset: 1px;'
    : '';

  // Status line: Available or Pro number
  const statusText = chassis.active_pronumber
    ? `Pro: ${chassis.active_pronumber}`
    : 'Available';
  const statusColor = chassis.active_pronumber ? '#dbeafe' : '#dcfce7';
  const statusTextColor = chassis.active_pronumber ? '#1e40af' : '#166534';

  // Truck badge (above the pill): matched truck unit #, colored by match confidence.
  let truckBadgeHtml = '';
  if (chassis.matched_truck_identifier) {
    const [truckBg, truckFg] =
      chassis.match_confidence === 'confirmed' ? ['#dcfce7', '#166534']   // green
      : chassis.match_confidence === 'probable' ? ['#fef3c7', '#92400e']  // amber
      : ['#e5e7eb', '#6b7280'];                                           // stale / grey
    truckBadgeHtml = `<div style="display:inline-block;padding:1px 6px;border-radius:9999px;font-size:9px;font-weight:700;font-family:var(--app-font-family);white-space:nowrap;background:${truckBg};color:${truckFg};border:1px solid rgba(0,0,0,0.15);margin-bottom:1px;">🚛 ${chassis.matched_truck_identifier}</div>`;
  }

  container.innerHTML = `
    ${truckBadgeHtml}
    <div style="display:inline-block;padding:2px 6px;border-radius:9999px;font-size:11px;font-weight:700;font-family:var(--app-font-family);white-space:nowrap;background:${color};color:#fff;border:1.5px solid rgba(0,0,0,0.25);${selectedStyle}">${label}</div>
    <div style="display:inline-block;padding:1px 4px;border-radius:4px;font-size:9px;font-weight:600;font-family:var(--app-font-family);white-space:nowrap;background:${statusColor};color:${statusTextColor};margin-top:1px;">${statusText}</div>
  `;
  return container;
}

// ============================================================================
// HELPERS
// ============================================================================

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ============================================================================
// FILTER SUB-COMPONENTS
// ============================================================================

function ThreeWayToggle({
  label,
  value,
  onChange,
  yesLabel = 'Yes',
  noLabel = 'No',
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  yesLabel?: string;
  noLabel?: string;
}) {
  const btnBase = 'px-2 py-0.5 text-xs font-medium rounded transition-colors';
  const active = 'bg-blue-600 text-white';
  const inactive = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600';
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
      <div className="flex gap-0.5">
        <button className={`${btnBase} ${value === null ? active : inactive}`} onClick={() => onChange(null)}>All</button>
        <button className={`${btnBase} ${value === true ? active : inactive}`} onClick={() => onChange(true)}>{yesLabel}</button>
        <button className={`${btnBase} ${value === false ? active : inactive}`} onClick={() => onChange(false)}>{noLabel}</button>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function ChassisMap() {
  // Contexts
  const { } = useDispatchFooter();
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();

  // Dark mode detection (same pattern as LiveMap)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  // Data state
  const [chassisList, setChassisList] = useState<ChassisListRow[]>([]);
  const [selectedChassis, setSelectedChassis] = useState<ChassisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Chassis list sidebar state
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [isListLocked, setIsListLocked] = useState(false);
  const [showDesktopSidebar, setShowDesktopSidebar] = useState(true);

  // Order details drawer state
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);


  // Filter chip popup state
  // Popup states for Status/Environment chips
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [showEnvironmentPopup, setShowEnvironmentPopup] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedMakes, setSelectedMakes] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [selectedChassisTypes, setSelectedChassisTypes] = useState<string[]>([]);
  const [selectedDoorTypes, setSelectedDoorTypes] = useState<string[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<string[]>([]); // 'available', 'on_a_pro'
  const [filterMoving, setFilterMoving] = useState<boolean | null>(null);
  const [filterContents, setFilterContents] = useState<boolean | null>(null);
  const [filterDoorOpen, setFilterDoorOpen] = useState<boolean | null>(null);
  const [filterBattery, setFilterBattery] = useState<boolean | null>(null);
  const [tempRange, setTempRange] = useState<[number | null, number | null]>([null, null]);
  const [humidityRange, setHumidityRange] = useState<[number | null, number | null]>([null, null]);

  // Derived filter options as MultiSelectOption arrays
  const categoryOptions: MultiSelectOption[] = useMemo(() =>
    [...new Set(chassisList.map(c => c.equip_category).filter(Boolean))].sort().map(v => ({ value: v!, label: v! })),
    [chassisList]
  );
  const makeOptions: MultiSelectOption[] = useMemo(() =>
    [...new Set(chassisList.map(c => c.equip_make).filter(Boolean))].sort().map(v => ({ value: v!, label: v! })),
    [chassisList]
  );
  const modelOptions: MultiSelectOption[] = useMemo(() =>
    [...new Set(chassisList.map(c => c.equip_model).filter(Boolean))].sort().map(v => ({ value: v!, label: v! })),
    [chassisList]
  );
  const chassisTypes = useMemo(() =>
    [...new Set(chassisList.map(c => c.chassis_type).filter(Boolean))] as string[],
    [chassisList]
  );
  const doorTypes = useMemo(() =>
    [...new Set(chassisList.map(c => c.door_type).filter(Boolean))] as string[],
    [chassisList]
  );

  // Filtered chassis list
  const filteredChassis = useMemo(() => {
    return chassisList.filter(c => {
      if (!c.latitude || !c.longitude) return false;
      if (searchQuery && !c.identifier?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (selectedCategories.length > 0 && !selectedCategories.includes(c.equip_category || '')) return false;
      if (selectedMakes.length > 0 && !selectedMakes.includes(c.equip_make || '')) return false;
      if (selectedModels.length > 0 && !selectedModels.includes(c.equip_model || '')) return false;
      if (selectedChassisTypes.length > 0 && !selectedChassisTypes.includes(c.chassis_type || '')) return false;
      if (selectedDoorTypes.length > 0 && !selectedDoorTypes.includes(c.door_type || '')) return false;
      if (selectedAssignment.length > 0) {
        const hasActivePro = c.active_pronumber != null;
        const matchesAvailable = selectedAssignment.includes('available') && !hasActivePro;
        const matchesOnPro = selectedAssignment.includes('on_a_pro') && hasActivePro;
        if (!matchesAvailable && !matchesOnPro) return false;
      }
      if (filterMoving !== null && c.is_moving !== filterMoving) return false;
      if (filterContents !== null && c.has_contents !== filterContents) return false;
      if (filterDoorOpen !== null && c.door_open !== filterDoorOpen) return false;
      if (filterBattery !== null) {
        if (filterBattery && c.battery_state !== true) return false;
        if (!filterBattery && c.battery_state !== false) return false;
      }
      if (tempRange[0] !== null && (c.temperature === null || c.temperature < tempRange[0])) return false;
      if (tempRange[1] !== null && (c.temperature === null || c.temperature > tempRange[1])) return false;
      if (humidityRange[0] !== null && (c.humidity === null || c.humidity < humidityRange[0])) return false;
      if (humidityRange[1] !== null && (c.humidity === null || c.humidity > humidityRange[1])) return false;
      return true;
    });
  }, [chassisList, searchQuery, selectedCategories, selectedMakes, selectedModels, selectedChassisTypes, selectedDoorTypes, selectedAssignment, filterMoving, filterContents, filterDoorOpen, filterBattery, tempRange, humidityRange]);

  // Clear all filters handler
  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedCategories([]);
    setSelectedMakes([]);
    setSelectedModels([]);
    setSelectedChassisTypes([]);
    setSelectedDoorTypes([]);
    setSelectedAssignment([]);
    setFilterMoving(null);
    setFilterContents(null);
    setFilterDoorOpen(null);
    setFilterBattery(null);
    setTempRange([null, null]);
    setHumidityRange([null, null]);
  }, []);

  // Split chassis into "in view" and "outside view" based on map bounds
  const chassisInView = useMemo(() => {
    if (isListLocked) return filteredChassis;
    if (!mapBounds) return filteredChassis;
    return filteredChassis.filter(c => {
      if (c.latitude == null || c.longitude == null) return false;
      return mapBounds.contains(new google.maps.LatLng(c.latitude, c.longitude));
    });
  }, [filteredChassis, mapBounds, isListLocked]);

  const chassisOutsideView = useMemo(() => {
    if (isListLocked) return [];
    if (!mapBounds) return [];
    return filteredChassis.filter(c => {
      if (c.latitude == null || c.longitude == null) return false;
      return !mapBounds.contains(new google.maps.LatLng(c.latitude, c.longitude));
    });
  }, [filteredChassis, mapBounds, isListLocked]);

  // Select chassis and pan map to it
  const selectAndPanToChassis = useCallback((chassis: ChassisData) => {
    setSelectedChassis(chassis);
    if (mapRef.current && chassis.latitude != null && chassis.longitude != null) {
      mapRef.current.panTo({ lat: chassis.latitude, lng: chassis.longitude });
      const currentZoom = mapRef.current.getZoom();
      if (currentZoom != null && currentZoom < 13) {
        mapRef.current.setZoom(13);
      }
    }
  }, []);

  // Open order details drawer for a PRO number
  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Map ref
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);

  // Google Maps loader
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // Listen for dark mode changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const savedTheme = localStorage.getItem('theme');
      setIsDarkMode(
        savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)
      );
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Fetch chassis data
  const fetchChassis = useCallback(async () => {
    try {
      const response = await api.get<ChassisData[]>('/bb-radar/chassis');
      // Equipment/move joins can repeat an asset, including the same PRO.
      // Assign unique row keys before filtering so React removes stale rows
      // correctly and each row keeps its identity as filters/bounds change.
      setChassisList(response.data.map((chassis, index) => ({
        ...chassis,
        rowKey: `${chassis.asset_id}-${index}`,
      })));
      setError(null);
    } catch (err) {
      console.error('Failed to fetch chassis data:', err);
      setError('Failed to load chassis data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchChassis();
  }, [fetchChassis]);

  // Background refresh
  useVisibilityAwareInterval(fetchChassis, REFRESH_INTERVAL_MS);

  // Map load callback
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    // Clean up existing clusterer and markers if map is remounting
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.setMap(null);
      clustererRef.current = null;
    }
    markersRef.current.forEach(marker => { marker.map = null; });
    markersRef.current = [];

    // Track map bounds for chassis list filtering
    map.addListener('idle', () => {
      const bounds = map.getBounds();
      if (bounds) setMapBounds(bounds);
    });

    // Initialize clusterer with custom renderer
    clustererRef.current = new MarkerClusterer({
      map,
      markers: [],
      renderer: {
        render: ({ count, position }: { count: number; position: google.maps.LatLng }) => {
          if (count < 5) {
            const emptyDiv = document.createElement('div');
            emptyDiv.style.width = '1px';
            emptyDiv.style.height = '1px';
            emptyDiv.style.opacity = '0';
            return new google.maps.marker.AdvancedMarkerElement({ position, content: emptyDiv });
          }
          const clusterDiv = document.createElement('div');
          clusterDiv.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="50" height="50">
              <circle cx="50" cy="50" r="45" fill="#6B7280" stroke="#030712" stroke-width="3" opacity="0.85"/>
              <text x="50" y="50" text-anchor="middle" dominant-baseline="central"
                    font-family="var(--app-font-family)" font-size="24" font-weight="bold" fill="white">
                ${count}
              </text>
            </svg>
          `;
          return new google.maps.marker.AdvancedMarkerElement({ position, content: clusterDiv });
        },
      },
      onClusterClick: (event: any, cluster: any, map: google.maps.Map) => {
        if (cluster.bounds) {
          map.fitBounds(cluster.bounds);
        }
      },
    });
  }, []);

  // Track whether we've done the initial bounds fit
  const hasFittedBoundsRef = useRef(false);

  // Sync markers when filtered chassis data or selection changes
  useEffect(() => {
    if (!mapRef.current || !clustererRef.current) return;

    // Clear old markers
    markersRef.current.forEach(marker => { marker.map = null; });
    markersRef.current = [];
    clustererRef.current.clearMarkers();

    const selectedId = selectedChassis?.asset_id ?? null;
    const newMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;

    filteredChassis.forEach(chassis => {
      if (chassis.latitude == null || chassis.longitude == null) return;

      const isSelected = chassis.asset_id === selectedId;
      const content = createChassisMarkerContent(chassis, isSelected);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: chassis.latitude, lng: chassis.longitude },
        content,
        zIndex: isSelected ? 1000 : 1,
      }) as any;

      marker.assetId = chassis.asset_id;

      marker.addListener('click', () => {
        setSelectedChassis(chassis);
      });

      newMarkers.push(marker);
      bounds.extend({ lat: chassis.latitude, lng: chassis.longitude });
      hasBounds = true;
    });

    markersRef.current = newMarkers;

    // Selected marker renders directly on map (not in clusterer) so it stays visible
    const markersForClusterer = newMarkers.filter(m => (m as any).assetId !== selectedId);
    newMarkers.forEach(m => {
      if ((m as any).assetId === selectedId) {
        m.map = mapRef.current;
      }
    });

    clustererRef.current.addMarkers(markersForClusterer);

    // Fit bounds only on initial load
    if (hasBounds && !hasFittedBoundsRef.current) {
      mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      hasFittedBoundsRef.current = true;
    }
  }, [filteredChassis, selectedChassis]);

  // Cleanup markers on unmount
  useEffect(() => {
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
      }
      markersRef.current.forEach(marker => { marker.map = null; });
    };
  }, []);

  return (
    <div className={styles.pageContainer}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <GridLabel areaName="pageHeader" position="top-left" />
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-heading">Chassis Map</h1>
          </div>
          <div className="flex items-center gap-2">
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <GridLabel areaName="filterBar" position="top-right" />
        <div className="flex items-center justify-between px-4 py-2 text-sm bg-panel border-b border-gray-200 dark:border-gray-700">
          {/* Left: MultiSelect filters + count */}
          <div className="flex items-center gap-2">
            <MultiSelect
              options={categoryOptions}
              value={selectedCategories}
              onChange={setSelectedCategories}
              placeholder="Category"
              size="sm"
              showClearOption
              clearOptionLabel="All Categories"
              usePortal
            />
            <MultiSelect
              options={makeOptions}
              value={selectedMakes}
              onChange={setSelectedMakes}
              placeholder="Make"
              size="sm"
              showClearOption
              clearOptionLabel="All Makes"
              enableSearch
              searchPlaceholder="Search makes..."
              usePortal
            />
            <MultiSelect
              options={modelOptions}
              value={selectedModels}
              onChange={setSelectedModels}
              placeholder="Model"
              size="sm"
              showClearOption
              clearOptionLabel="All Models"
              enableSearch
              searchPlaceholder="Search models..."
              usePortal
            />

            <MultiSelect
              options={[
                { value: 'available', label: 'Available' },
                { value: 'on_a_pro', label: 'On a Pro' },
              ]}
              value={selectedAssignment}
              onChange={setSelectedAssignment}
              placeholder="Assignment"
              size="sm"
              showClearOption
              clearOptionLabel="All"
              usePortal
            />

            {/* Status Chip */}
            <div className="relative">
              <button
                onClick={() => { setShowStatusPopup(v => !v); setShowEnvironmentPopup(false); }}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${
                  [filterMoving, filterContents, filterDoorOpen, filterBattery].filter(v => v !== null).length > 0
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-blue-500'
                    : 'bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-500 hover:ring-gray-400'
                }`}
              >
                <span>Status</span>
                {[filterMoving, filterContents, filterDoorOpen, filterBattery].filter(v => v !== null).length > 0 && (
                  <span className="text-blue-500 dark:text-blue-400">({[filterMoving, filterContents, filterDoorOpen, filterBattery].filter(v => v !== null).length})</span>
                )}
              </button>
              {showStatusPopup && (
                <div className="absolute top-full right-0 mt-1 p-3 bg-panel rounded-lg shadow-lg ring-1 ring-gray-200 dark:ring-gray-700 z-50 min-w-[260px]">
                  <div className="space-y-2">
                    <ThreeWayToggle label="Moving" value={filterMoving} onChange={setFilterMoving} />
                    <ThreeWayToggle label="Has Contents" value={filterContents} onChange={setFilterContents} yesLabel="Loaded" noLabel="Empty" />
                    <ThreeWayToggle label="Door Open" value={filterDoorOpen} onChange={setFilterDoorOpen} yesLabel="Open" noLabel="Closed" />
                    <ThreeWayToggle label="Battery OK" value={filterBattery} onChange={setFilterBattery} yesLabel="OK" noLabel="Low" />
                  </div>
                  {[filterMoving, filterContents, filterDoorOpen, filterBattery].filter(v => v !== null).length > 0 && (
                    <button onClick={() => { setFilterMoving(null); setFilterContents(null); setFilterDoorOpen(null); setFilterBattery(null); }} className="mt-2 text-xs text-muted hover:text-gray-700 dark:hover:text-gray-200 underline">Clear all</button>
                  )}
                </div>
              )}
            </div>

            {/* Environment Chip */}
            <div className="relative">
              <button
                onClick={() => { setShowEnvironmentPopup(v => !v); setShowStatusPopup(false); }}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${
                  [tempRange[0], tempRange[1], humidityRange[0], humidityRange[1]].filter(v => v !== null).length > 0
                    ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 ring-rose-500'
                    : 'bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-500 hover:ring-gray-400'
                }`}
              >
                <span>Environment</span>
                {[tempRange[0], tempRange[1], humidityRange[0], humidityRange[1]].filter(v => v !== null).length > 0 && (
                  <span className="text-rose-500 dark:text-rose-400">({[tempRange[0], tempRange[1], humidityRange[0], humidityRange[1]].filter(v => v !== null).length})</span>
                )}
              </button>
              {showEnvironmentPopup && (
                <div className="absolute top-full right-0 mt-1 p-3 bg-panel rounded-lg shadow-lg ring-1 ring-gray-200 dark:ring-gray-700 z-50 min-w-[240px]">
                  <div className="mb-3">
                    <div className="text-xs font-medium text-muted mb-1.5">Temperature (°C)</div>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        placeholder="Min"
                        value={tempRange[0] ?? ''}
                        onChange={e => setTempRange([e.target.value === '' ? null : Number(e.target.value), tempRange[1]])}
                        className="w-full py-1 px-2 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-400">-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={tempRange[1] ?? ''}
                        onChange={e => setTempRange([tempRange[0], e.target.value === '' ? null : Number(e.target.value)])}
                        className="w-full py-1 px-2 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted mb-1.5">Humidity (%)</div>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        placeholder="Min"
                        value={humidityRange[0] ?? ''}
                        onChange={e => setHumidityRange([e.target.value === '' ? null : Number(e.target.value), humidityRange[1]])}
                        className="w-full py-1 px-2 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-400">-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={humidityRange[1] ?? ''}
                        onChange={e => setHumidityRange([humidityRange[0], e.target.value === '' ? null : Number(e.target.value)])}
                        className="w-full py-1 px-2 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  {[tempRange[0], tempRange[1], humidityRange[0], humidityRange[1]].filter(v => v !== null).length > 0 && (
                    <button onClick={() => { setTempRange([null, null]); setHumidityRange([null, null]); }} className="mt-2 text-xs text-muted hover:text-gray-700 dark:hover:text-gray-200 underline">Clear all</button>
                  )}
                </div>
              )}
            </div>

            {/* Clear All */}
            {(selectedCategories.length + selectedMakes.length + selectedModels.length + selectedAssignment.length + selectedChassisTypes.length + selectedDoorTypes.length +
              [filterMoving, filterContents, filterDoorOpen, filterBattery].filter(v => v !== null).length +
              [tempRange[0], tempRange[1], humidityRange[0], humidityRange[1]].filter(v => v !== null).length) > 0 && (
              <button
                onClick={clearAllFilters}
                className="px-2 py-1 text-xs font-medium text-muted hover:text-gray-700 dark:hover:text-gray-200 underline whitespace-nowrap"
              >
                Clear All
              </button>
            )}

            <span className="text-gray-600 dark:text-gray-400 font-semibold text-xs whitespace-nowrap ml-1">
              {loading ? 'Loading...' : `${filteredChassis.length} of ${chassisList.length} Chassis`}
            </span>
          </div>

          {/* Right: search */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search chassis..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-7 pr-7 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content: 2-panel grid */}
      <div className={styles.mainContent}>
        {/* Map */}
        <div className={styles.map}>
          <GridLabel areaName="map" position="top-left" />
          {loadError ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-red-500 dark:text-red-400 mb-2">Failed to load Google Maps</p>
                <p className="text-sm text-muted">{loadError.message}</p>
              </div>
            </div>
          ) : !isLoaded ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className="text-sm text-muted">Loading map...</p>
              </div>
            </div>
          ) : (
            <GoogleMap
              key={`chassis-map-${isDarkMode ? 'dark' : 'light'}`}
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={11}
              options={getMapOptions(isDarkMode)}
              onLoad={onMapLoad}
            >
              {/* Markers managed by MarkerClusterer via useEffect */}
            </GoogleMap>
          )}
          {/* Status Legend */}
          <div className={`${styles.legendBar} ${isDarkMode ? 'bg-gray-800/90 text-gray-200' : 'bg-white/90 text-gray-700'}`}>
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#10B981' }}></span> Moving</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#6B7280' }}></span> Stationary</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#F59E0B' }}></span> Door Open</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#EF4444' }}></span> Low Battery</span>
          </div>
        </div>

        {/* Chassis List - Right side */}
        <div className={`${styles.chassisList} ${!showDesktopSidebar ? styles.chassisListCollapsed : ''} hidden lg:block relative overflow-hidden`}>
          {/* Collapse/Expand Handle */}
          <button
            onClick={() => setShowDesktopSidebar(!showDesktopSidebar)}
            className="absolute -left-7 top-[18px] z-20 bg-panel border border-r-0 border-gray-200 dark:border-gray-700 rounded-l-lg py-2 px-1.5 hover:bg-highlight transition-colors shadow-sm"
            aria-label={showDesktopSidebar ? "Collapse sidebar" : "Expand sidebar"}
          >
            <ChevronRightIcon
              className={`w-4 h-4 text-gray-600 dark:text-gray-400 ${showDesktopSidebar ? 'rotate-0' : 'rotate-180'}`}
            />
          </button>

          {/* Sidebar Content */}
          <div className={`h-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-hidden ${
            showDesktopSidebar ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}>
            <div className="h-full flex flex-col overflow-hidden">
              {/* Sticky header */}
              <div className="flex-shrink-0 p-3 border-b border-gray-200 dark:border-gray-700 bg-panel">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-heading">
                    Chassis List ({filteredChassis.length})
                  </h2>
                  <button
                    onClick={() => setIsListLocked(!isListLocked)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      isListLocked
                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'text-muted hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    aria-label={isListLocked ? "Unlock chassis list" : "Lock chassis list"}
                    title={isListLocked ? "List locked — showing all chassis" : "List unlocked — showing chassis in view"}
                  >
                    {isListLocked ? (
                      <LockClosedIcon className="w-4 h-4" />
                    ) : (
                      <LockOpenIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Scrollable chassis list */}
              <div className="flex-1 overflow-y-auto">
                {filteredChassis.length === 0 ? (
                  <div className="p-4 text-center text-muted text-sm">
                    No chassis to display
                  </div>
                ) : (
                  <>
                    {/* In View Section */}
                    {!isListLocked && (
                      <div className="sticky top-0 z-10 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 border-b border-rule">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">In View ({chassisInView.length})</span>
                      </div>
                    )}
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {chassisInView.map((chassis) => (
                        <button
                          key={chassis.rowKey}
                          onClick={() => selectAndPanToChassis(chassis)}
                          className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            selectedChassis?.asset_id === chassis.asset_id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: getChassisColor(chassis) }}
                              />
                              <span className="text-sm font-medium text-heading truncate">
                                {chassis.identifier}
                              </span>
                            </div>
                            <span className={`text-xs font-medium flex-shrink-0 px-1.5 py-0.5 rounded ${
                              chassis.active_pronumber
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            }`}>
                              {chassis.active_pronumber ? `Pro: ${chassis.active_pronumber}` : 'Available'}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center justify-between text-xs text-muted">
                            <span>{chassis.equip_model || chassis.equip_category || '—'}</span>
                            <span>{chassis.last_reported_at ? timeAgo(chassis.last_reported_at) : '—'}</span>
                          </div>
                          {chassis.matched_truck_identifier && (
                            <div className={`mt-0.5 text-xs font-medium ${chassis.match_confidence ? CONFIDENCE_CLASS[chassis.match_confidence] ?? '' : ''}`}>
                              Truck {chassis.matched_truck_identifier}{chassis.match_confidence ? ` (${chassis.match_confidence})` : ''}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Outside View Section */}
                    {!isListLocked && chassisOutsideView.length > 0 && (
                      <>
                        <div className="sticky top-0 z-10 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 border-b border-rule">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Outside View ({chassisOutsideView.length})</span>
                        </div>
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {chassisOutsideView.map((chassis) => (
                            <button
                              key={chassis.rowKey}
                              onClick={() => selectAndPanToChassis(chassis)}
                              className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors opacity-60 ${
                                selectedChassis?.asset_id === chassis.asset_id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 opacity-100' : ''
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: getChassisColor(chassis) }}
                                  />
                                  <span className="text-sm font-medium text-heading truncate">
                                    {chassis.identifier}
                                  </span>
                                </div>
                                <span className={`text-xs font-medium flex-shrink-0 px-1.5 py-0.5 rounded ${
                                  chassis.active_pronumber
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                }`}>
                                  {chassis.active_pronumber ? `Pro: ${chassis.active_pronumber}` : 'Available'}
                                </span>
                              </div>
                              <div className="mt-1 flex items-center justify-between text-xs text-muted">
                                <span>{chassis.equip_model || chassis.equip_category || '—'}</span>
                                <span>{chassis.last_reported_at ? timeAgo(chassis.last_reported_at) : '—'}</span>
                              </div>
                              {chassis.matched_truck_identifier && (
                                <div className={`mt-0.5 text-xs font-medium ${chassis.match_confidence ? CONFIDENCE_CLASS[chassis.match_confidence] ?? '' : ''}`}>
                                  Truck {chassis.matched_truck_identifier}{chassis.match_confidence ? ` (${chassis.match_confidence})` : ''}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Chassis Details Drawer */}
      <Drawer
        isOpen={!!selectedChassis}
        onClose={() => setSelectedChassis(null)}
        title={selectedChassis?.identifier || 'Chassis Details'}
        position="right"
        maxWidth="sm"
        noBackdrop
      >
        {selectedChassis && (
          <div className="flex flex-col gap-3">
            {/* Equipment Info */}
            <div>
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Equipment</h3>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Category</span>
                  <span className="text-body">{selectedChassis.equip_category || '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Make</span>
                  <span className="text-body">{selectedChassis.equip_make || '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Model</span>
                  <span className="text-body">{selectedChassis.equip_model || '—'}</span>
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div>
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Assignment</h3>
              <div className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted">Status</span>
                  <span className={`font-medium ${selectedChassis.active_pronumber ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
                    {selectedChassis.active_pronumber ? 'In Use' : 'Available'}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted">Truck</span>
                  <span className={`font-medium ${selectedChassis.match_confidence ? CONFIDENCE_CLASS[selectedChassis.match_confidence] ?? '' : 'text-body'}`}>
                    {matchChipText(selectedChassis)}
                  </span>
                </div>
                {selectedChassis.active_pronumber && (
                  <>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-muted">PRO</span>
                      <button
                        onClick={() => openOrderDrawer(selectedChassis.active_pronumber!)}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        {selectedChassis.active_pronumber}
                      </button>
                    </div>
                    {selectedChassis.active_container && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted">Container</span>
                        <span className="text-body">{selectedChassis.active_container}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Identity */}
            <div>
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Radar Info</h3>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Chassis Type</span>
                  <span className="text-body">{selectedChassis.chassis_type || '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Door Type</span>
                  <span className="text-body">{selectedChassis.door_type || '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Asset Class</span>
                  <span className="text-body">{selectedChassis.asset_class || '—'}</span>
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Location</h3>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Latitude</span>
                  <span className="text-body">{selectedChassis.latitude?.toFixed(4) ?? '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Longitude</span>
                  <span className="text-body">{selectedChassis.longitude?.toFixed(4) ?? '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Altitude</span>
                  <span className="text-body">{selectedChassis.altitude != null ? `${selectedChassis.altitude.toFixed(1)} m` : '—'}</span>
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Status</h3>
              <div className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted">Moving</span>
                  <span className="flex items-center gap-1 text-body">
                    <span className={`inline-block w-2 h-2 rounded-full ${selectedChassis.is_moving ? 'bg-green-500' : 'bg-gray-400'}`} />
                    {selectedChassis.is_moving ? 'Yes' : 'No'}
                    {selectedChassis.is_moving && selectedChassis.velocity != null && (
                      <span className="text-muted ml-1">
                        ({Math.round(selectedChassis.velocity * 2.237)} mph)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted">Door</span>
                  <span className="flex items-center gap-1 text-body">
                    <span className={`inline-block w-2 h-2 rounded-full ${selectedChassis.door_open ? 'bg-amber-500' : 'bg-gray-400'}`} />
                    {selectedChassis.door_open ? 'Open' : 'Closed'}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted">Contents</span>
                  <span className="text-body">
                    {selectedChassis.has_contents ? (
                      <>Loaded{selectedChassis.contents_percentage != null && ` (${selectedChassis.contents_percentage}%)`}</>
                    ) : (
                      'Empty'
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted">Battery</span>
                  <span className="flex items-center gap-1 text-body">
                    <span className={`inline-block w-2 h-2 rounded-full ${selectedChassis.battery_state === false ? 'bg-red-500' : 'bg-green-500'}`} />
                    {selectedChassis.battery_state === false ? 'Low' : 'OK'}
                  </span>
                </div>
              </div>
            </div>

            {/* Environment */}
            <div>
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Environment</h3>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Temperature</span>
                  <span className="text-body">
                    {selectedChassis.temperature != null
                      ? `${selectedChassis.temperature.toFixed(1)}°C / ${(selectedChassis.temperature * 9 / 5 + 32).toFixed(1)}°F`
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Humidity</span>
                  <span className="text-body">
                    {selectedChassis.humidity != null ? `${selectedChassis.humidity.toFixed(1)}%` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div>
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Timestamps</h3>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Last Reported</span>
                  <span
                    className="text-body"
                    title={selectedChassis.last_reported_at ? new Date(selectedChassis.last_reported_at).toLocaleString() : ''}
                  >
                    {selectedChassis.last_reported_at ? timeAgo(selectedChassis.last_reported_at) : '—'}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Last Synced</span>
                  <span
                    className="text-body"
                    title={selectedChassis.last_synced_at ? new Date(selectedChassis.last_synced_at).toLocaleString() : ''}
                  >
                    {selectedChassis.last_synced_at ? timeAgo(selectedChassis.last_synced_at) : '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Order Details Drawer */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}
